import type { UserDTO, UserExpandedDTO } from "@/api/vtnz-backend/user";

export default class User {
  // -------------------------------------------------------------------- //
  // ------------------------------- State ------------------------------ //
  // -------------------------------------------------------------------- //

  /** Unique identifier in the DB for this user */
  id: string;

  /** The users first name. */
  firstName?: string;

  /** The users last name. */
  lastName?: string;

  /** The users display name. */
  displayName?: string;

  /** The users email address. */
  email: string;

  /** The users avatar URL if they have one. */
  avatarURL?: string | null;

  /** Flag that indicates if this is the current user */
  currentUserFlag: boolean;

  /**
   * Indicates whether the current user has an associated fleet.
   *
   * Used to determine whether the user has completed initial fleet setup.
   * This is derived from the existence of a fleet relationship, not from
   * fleet data being loaded.
   */
  inFleetFlag: boolean;

  // -------------------------------------------------------------------- //
  // --------------------------- Constructor ---------------------------- //
  // -------------------------------------------------------------------- //

  /**
   * Creates a new `User` class instance.
   *
   * @param {Object} [options] - required init options.
   * @param {string} [options.id] - Unique identifier in the DB for this user .
   * @param {string} [options.firstName] - The users first name.
   * @param {string} [options.lastName] - The users last name.
   * @param {string} [options.displayName] - The users display name.
   * @param {string} [options.email] - The users email address.
   * @param {string | null | undefined} [options.avatarURL] - The users avatar URL if they have one.
   * @param {boolean | undefined} [options.currentUserFlag] - Flag indicating if this is the current user.
   * @param {boolean | undefined} [options.inFleetFlag] - Flag indicating that the user is currently in a fleet.
   */
  constructor({
    id,
    firstName = undefined,
    lastName = undefined,
    displayName = undefined,
    email,
    avatarURL = undefined,
    currentUserFlag = false,
    inFleetFlag = false,
  }: {
    /** Unique identifier in the DB for this user */
    id: string;
    /** The users first name. */
    firstName?: string;
    /** The users last name. */
    lastName?: string;
    /** The users display name. */
    displayName?: string;
    /** The users email address. */
    email: string;
    /** The users avatar URL if they have one. */
    avatarURL?: string | null;
    /** Flag that indicates if this is the current user */
    currentUserFlag?: boolean;
    /**
     * Indicates whether the current user has an associated fleet.
     *
     * Used to determine whether the user has completed initial fleet setup.
     * This is derived from the existence of a fleet relationship, not from
     * fleet data being loaded.
     */
    inFleetFlag?: boolean;
  }) {
    this.id = id;
    this.email = email;
    this.firstName = firstName;
    this.lastName = lastName;
    this.displayName = displayName;
    this.avatarURL = avatarURL;
    this.currentUserFlag = currentUserFlag;
    this.inFleetFlag = inFleetFlag;
  }

  /**
   * Creates a new user form a DTO
   * @param {UserExpandedDTO} data an expanded user DTO
   * @returns {User} a new User
   */
  static fromDTO(data: UserExpandedDTO): User {
    // Nullable columns arrive as `null`; the domain model uses `undefined`.
    return new User({
      email: data.email,
      id: data.id,
      avatarURL: data.avatar_url,
      currentUserFlag: data.current_user,
      displayName: data.display_name ?? undefined,
      firstName: data.first_name ?? undefined,
      lastName: data.last_name ?? undefined,
      inFleetFlag: !!data.fleet_id,
    });
  }

  // -------------------------------------------------------------------- //
  // ------------------------------ Getters ----------------------------- //
  // -------------------------------------------------------------------- //

  /** Returns the users full name in a displayable string. */
  public get fullName(): string {
    if (this.firstName && this.lastName) return this.firstName + " " + this.lastName;
    if (this.displayName) return this.displayName;
    return (this.firstName ? this.firstName + " " : "") + (this.lastName ?? "");
  }

  // -------------------------------------------------------------------- //
  // ------------------------------ Methods ----------------------------- //
  // -------------------------------------------------------------------- //

  public updateData(data: Partial<UserDTO>) {
    if (data.avatar_url) this.avatarURL = data.avatar_url;
    if (data.display_name) this.displayName = data.display_name;
    if (data.first_name) this.firstName = data.first_name;
    if (data.last_name) this.lastName = data.last_name;
  }
}
